// File: src/features/coach/components/CoachOnboardingModal.tsx
import React, { useState } from 'react';
import Modal from '../../../components/common/Modal';
import CoachProfileCompletionForm from './CoachProfileCompletionForm';
import { CoachProfileUpdatePayload } from '../../../services/coachService';
import coachService from '../../../services/coachService';
import authService from '../../../services/authService';
import { toast } from 'react-hot-toast';
import { useUserProfile } from '../../../hooks/useUserProfile';
import { useAuth } from '../../../hooks/useAuth';
import ImageUploadAndCrop from './ImageUploadAndCrop';

interface CoachOnboardingModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const CoachOnboardingModal: React.FC<CoachOnboardingModalProps> = ({ isOpen, onClose }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const { fetchProfile } = useUserProfile();
    const { user, loadUser } = useAuth();

    const handleSubmit = async (data: CoachProfileUpdatePayload) => {
        setIsSubmitting(true);
        try {
            await coachService.updateMyProfile(data);
            toast.success("Your profile has been updated!");
            await fetchProfile(); // Refresh profile data in context
            onClose();
        } catch (error) {
            toast.error("Failed to update your profile. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleImageUpload = async (file: File) => {
        setIsUploading(true);
        try {
            await authService.uploadProfilePicture(file);
            await loadUser(); // Refresh user context to get new image URL
            toast.success("Profile picture updated!");
        } catch (error) {
            toast.error("Failed to upload profile picture.");
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Welcome! Complete Your Profile"
            size="lg"
        >
            <p className="text-sm text-center text-neutral-dark mb-4">
                Let's set up your coaching profile. This information will help clients get to know you. You can always edit this later from your profile page.
            </p>
            <div className="flex justify-center mb-6">
                <ImageUploadAndCrop 
                    onFileCropped={handleImageUpload}
                    currentImageUrl={user?.profile_picture_url}
                    isUploading={isUploading}
                />
            </div>
            <CoachProfileCompletionForm
                onSubmit={handleSubmit}
                onSkip={onClose}
                isLoading={isSubmitting}
            />
        </Modal>
    );
};

export default CoachOnboardingModal;